import './productspage.css';
import { ShoppingCart, Eye, Heart, X,Mail } from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from 'react';
import { useCurrency } from '../Currencycontext';

function Products() {
  const { currency, convertPrice } = useCurrency();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [expandedCategory, setExpandedCategory] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [userCartIds, setUserCartIds] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [ratings, setRatings] = useState({});
  const [pendingCartItem, setPendingCartItem] = useState(null);
  const [message, setMessage] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Load config.json
  useEffect(() => {
    fetch("/config.json")
      .then(res => res.json())
      .then(data => setConfig(data))
      .catch(err => console.error("Failed to load config.json:", err));
  }, []);
    useEffect(() => {
      if (!config) return;
  
      fetch(`${config.BASE_URL}${config.CHECK_SESSION}`, {
        method: "POST",
        credentials: "include",
      })
        .then(res => res.json())
        .then(data => {
          if (data.active) {
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(false);
          }
        })
        .catch(() => setIsLoggedIn(false));
    }, [config]);

  // Fetch wishlist
  useEffect(() => {
    if (!config || !isLoggedIn) return;
    fetch(`${config.BASE_URL}${config.GET_WISHLIST}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.wishlist) {
          setWishlistIds(data.wishlist.map(item => item.product_id));
        }
      });
  }, [config,isLoggedIn]);

  // Fetch cart
  useEffect(() => {
    if (!config  || !isLoggedIn) return;
    fetch(`${config.BASE_URL}${config.GET_CART}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.cart) {
          setUserCartIds(data.cart.map(item => item.product_id));
        }
      });
  }, [config,isLoggedIn]);

  // Fetch products
  useEffect(() => {
    if (!config) return;
    fetch(`${config.BASE_URL}${config.GET_PRODUCTS}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success) setProducts(data.products);
      });
  }, [config]);

  // Fetch ratings
  useEffect(() => {
    if (!config || products.length === 0) return;
    fetch(`${config.BASE_URL}${config.GET_PRODUCT_RATINGS}`, { method: "POST", credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.ratings) {
          const ratingsMap = {};
          for (let pid in data.ratings) {
            ratingsMap[pid] = parseFloat(data.ratings[pid].avg_rating || 0);
          }
          setRatings(ratingsMap);
        }
      })
      .catch(err => console.error("Failed to fetch ratings:", err));
  }, [config, products]);

  // Categories list
  useEffect(() => {
    const academicSubs = [
      ...new Set(
        products
          .filter(p => p.category_name === "Academic Projects")
          .map(p => p.domain_name)
      )
    ];
    setSubCategories(academicSubs);

    const uniqueDomains = [
      ...new Set(products.map(p => p.domain_name))
    ];
   
    const filteredCats = uniqueDomains.filter(
      cat => !academicSubs.includes(cat) && cat !== "Academic Projects"
    );
    setCategories(filteredCats);
  }, [products]);

  
  // Add to cart
  useEffect(() => {
    if (!pendingCartItem || !config) return;
    const formData = new FormData();
    Object.entries(pendingCartItem).forEach(([k, v]) => formData.append(k, v));

    fetch(`${config.BASE_URL}${config.ADD_TO_CART}`, { method: "POST", body: formData, credentials: "include" })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setUserCartIds(prev => [...prev, pendingCartItem.product_id]);
          let count = Number(window.cartCount || localStorage.getItem("cartCount") || 0);
          count += 1;
          window.cartCount = count;
          localStorage.setItem("cartCount", count);
          window.dispatchEvent(new Event("cartUpdated"));
          setMessage({ type: "success", text: "Added to cart successfully!" });
        } else {
          setMessage({ type: "error", text: data.message || "Failed to add to cart" });
        }
      })
      .finally(() => setPendingCartItem(null));
  }, [pendingCartItem, config]);

  const toggleWishlist = (product_id) => {
  if (!config) return;
    if (!isLoggedIn) {
    setMessage({ type: "error", text: "Please log in to use the wishlist." });
    return;
  }

  fetch(`${config.BASE_URL}${config.TOGGLE_WISHLIST}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ product_id })
  })
    .then(res => res.json())
    .then(data => {
      if (data.success) {
        if (data.action === "added") {
          setWishlistIds(prev => [...prev, product_id]);
          setMessage({ type: "success", text: "Added to wishlist" });
        } else if (data.action === "removed") {
          setWishlistIds(prev => prev.filter(id => id !== product_id));
          setMessage({ type: "info", text: "Removed from wishlist" });
        }
      } else {
        
        setMessage({ type: "error", text: data.message || "Something went wrong" });
      }
    })
    .catch(() => {
      setMessage({ type: "error", text: "Network error, please try again" });
    });
};


  // Auto-hide message
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Filtering logic
  const filteredProducts = products.filter(p => {
    const matchesCategory =
      selectedCategory === "all" ||
      p.domain_name === selectedCategory ||
      (selectedCategory === "Academic Projects" && p.category_name === "Academic Projects") ||
      (subCategories.includes(selectedCategory) &&
        p.category_name === "Academic Projects" &&
        p.domain_name === selectedCategory);

    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase());
    const totalPrice = parseFloat(p.price) + parseFloat(p.tax || 0);
    const matchesPrice = (minPrice === "" || totalPrice >= minPrice) && (maxPrice === "" || totalPrice <= maxPrice);
    return matchesCategory && matchesSearch && matchesPrice;
  });

  const academicProjects = filteredProducts.filter(p => p.category_name === "Academic Projects");



  return (
    <div className="products-page">
      {/* Floating message */}
     {message && (
  <div className={`floating-message ${message.type}`}>
    <span>{message.text}</span>
    <X
      size={18}
      style={{ marginLeft: "15px", cursor: "pointer" }}
      onClick={() => setMessage(null)}
    />
  </div>
)}


      {/* Sidebar */}
      <div className="sidebar">
        <h1 className="sidebar-heading">Categories</h1>
        <div className="categories-list">
          {/* All */}
          <div className={`category-item ${selectedCategory === "all" ? 'selected' : ''}`}
            onClick={() => setSelectedCategory("all")}>
            <h2 className="category-title">All</h2>
          </div>

          {/* Academic Projects expandable */}
          <div>
            <div
              className={`category-item ${selectedCategory === "Academic Projects" ? 'selected' : ''}`}
              onClick={() => {

                const hasAcademicProjects = products.some(p => p.category_name === "Academic Projects");
    
                  if (!hasAcademicProjects) {
                    // Redirect to /project-list with message
                    setMessage({ type: "info", text: "Redirecting to Project List..." });
                    setTimeout(() => navigate("/project-list"), 1500);
                    return;
                  }

                setSelectedCategory("Academic Projects");
                setExpandedCategory(!expandedCategory);
              }}
            >
              <h2 className="category-title">Academic Projects</h2>
            </div>
            {expandedCategory && (
              <div className="subcategory-list">
                {subCategories.map((sub, i) => (
                  <div
                    key={i}
                    className={`subcategory-item ${selectedCategory === sub ? 'selected' : ''}`}
                    onClick={() => setSelectedCategory(sub)}
                  >
                    <h3 className="subcategory-title">- {sub}</h3>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Other categories */}
          {categories.map((cat, i) => (
            <div key={i}
              className={`category-item ${selectedCategory === cat ? 'selected' : ''}`}
              onClick={() => setSelectedCategory(cat)}>
              <h2 className="category-title">{cat}</h2>
            </div>
          ))}

          <button
        className="customize-button"
        onClick={() => {
          setMessage({ type: "info", text: "Upload Your Query in the form below" });
          setTimeout(() => navigate("/contactus"), 1500);
        }}
      >
        Customise Robots
      </button>
        </div>
      </div>

      {/* Products */}
      <div className="featured-section">
        <div className="filter-container">
          <input type="text" placeholder="Search..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="search-input" />
          <div className="price-range">
            <input type="number" placeholder="Min Price" value={minPrice}  min={0} onChange={e => setMinPrice(e.target.value)} className="price-input" />
            <input type="number" placeholder="Max Price" value={maxPrice}   min={0} onChange={e => setMaxPrice(e.target.value)} className="price-input" />
          </div>
        </div>

        <h2 className="featured-heading">Featured Products</h2>

        <div className="featured-grid">
           {selectedCategory === "Academic Projects" && 
      filteredProducts.filter(p => p.category_name === "Academic Projects").length === 0 ? (
        <div className="no-academic-projects">
          <p>
            Find Academic Projects in the{" "}
            <span
              className="services-link"
              onClick={() => navigate("/project-list")}
            >
              Project List 
            </span>{" "}
            Page.
          </p>
        </div>
  ) : (
          filteredProducts.map(product => {
            const inWishlist = wishlistIds.includes(product.product_id);
            const avgRating = ratings[product.product_id] || 0;
             const totalPrice = parseFloat(product.price || 0) + parseFloat(product.tax || 0);
            return (
              <div key={product.product_id} className="featured-card">
                <div className="featured-img-container"  onClick={() => navigate("/product-details", { state: { product } })}>
                  <img src={`/product_thumbnails/${product.product_id}/${product.image_path}`} alt={product.title} className="featured-img" />
                  <div className="featured-badge"  onClick={e => { 
                      e.stopPropagation(); 
                      toggleWishlist(product.product_id);
                    }}>
                    {inWishlist ? <Heart size={18} fill="red" color="red" /> : <Heart size={18} />}
                  </div>
                 <div className="featured-overlay">
              <div className="overlay-content">
                {product.status1 === 1 ? (
                  // Products that require a quote
                  <button
                    className="overlay-button add-to-cart"
                    onClick={e => {
                      e.stopPropagation();
                      navigate("/quotationform", { state: { product } });
                    }}
                  >
                    <Mail size={18} className="mr-1" /> Get Quote
                  </button>
                ) : (
                  // Products available for purchase
                  <button
                    className="overlay-button add-to-cart"
                    onClick={e => {
                      e.stopPropagation();
                      if (!isLoggedIn) {
                        setMessage({ type: "error", text: "Please log in to add items to cart." });
                        return;
                      }
                      setPendingCartItem({ product_id: product.product_id, product_title: product.title, quantity: 1 });
                    }}
                    disabled={userCartIds.includes(product.product_id)}
                  >
                    <ShoppingCart size={18} className="mr-1" /> {userCartIds.includes(product.product_id) ? "In Cart" : "Add to Cart"}
                  </button>
                )}

                <button
                  onClick={e => {
                    e.stopPropagation();
                    navigate("/product-details", { state: { product } });
                  }}
                  className="overlay-button view-details"
                >
                  <Eye size={18} className="mr-1" /> View Details
                </button>
              </div>
              </div>

                </div>

                <div className="featured-content">
                <h3 className="featured-title">{product.title}</h3>
                <div className="featured-rating">
                  <span className="stars">
                    {Array.from({ length: 5 }, (_, i) => (
                      <span key={i}>{i < Math.round(avgRating) ? "★" : "☆"}</span>
                    ))}
                  </span>
                  <span className="rating-value">{avgRating.toFixed(1)}</span>
                </div>

                 {product.status1 === 1 ? (
                <p className="tax-label">
                  Get Valid Quote To Purchase This Product
                </p>
              ) : (
                <p className="featured-price">
                  {currency === "INR" ? "₹" : "$"} {convertPrice(parseFloat(product.price || 0) + parseFloat(product.tax || 0))}
                  <span className="tax-label">(inclusive of tax)</span>
                </p>
              )}
              </div>

              </div>
            );
          })
          )}
        </div>
      </div>
    </div>
  );
}

export default Products;
